import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  IconButton,
  Button,
  Pagination,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  PushPin,
  PushPinOutlined,
  MarkunreadMailbox,
  Delete as DeleteIcon,
  MailOutline,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  usePinNotificationMutation,
} from "../../features/api/notificationApiSlice";

const MessageTable = ({
  messages,
  isLoading,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onMessageClick,
  page,
  totalPages,
  onPageChange,
  totalElements,
  category,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";
  const iconColor = process.env.REACT_APP_COLOR_WALLET_ICON || process.env.REACT_APP_COLOR_TEXT || (isLightTheme ? "#333333" : "white");
  const textColor = process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || (isLightTheme ? "#333333" : "white");
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [pinNotification] = usePinNotificationMutation();

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    await markAsRead(id);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await deleteNotification(id);
  };

  const handlePin = async (id, e) => {
    e.stopPropagation();
    await pinNotification(id);
  };

  const handleBulkMarkAsRead = async () => {
    await markAllAsRead(category);
  };

  const handleBulkDelete = async () => {
    const promises = Array.from(selectedIds).map((id) =>
      deleteNotification(id)
    );
    await Promise.all(promises);
  };

  const allSelected =
    messages.length > 0 && selectedIds.size === messages.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " " + date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Bulk actions bar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={1}
        py={1}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider1 || "#444"}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedIds.size > 0 && !allSelected}
            onChange={onSelectAll}
            size="small"
            sx={{
              color: iconColor,
              "& .MuiSvgIcon-root": { color: iconColor },
              "&.Mui-checked .MuiSvgIcon-root": { color: theme.palette.primary.main },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: textColor,
              fontSize: "13px",
            }}
          >
            {t("messaging.selectall", "Select / Unselect All")}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Button
            size="small"
            startIcon={<MailOutline sx={{ fontSize: 18, color: iconColor }} />}
            onClick={handleBulkMarkAsRead}
            sx={{
              color: iconColor,
              textTransform: "none",
              fontSize: "13px",
            }}
          >
            {t("messaging.markasread", "Mark As Read")}
          </Button>
          <Button
            size="small"
            startIcon={<DeleteIcon sx={{ fontSize: 18, color: iconColor }} />}
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
            sx={{
              color: iconColor,
              textTransform: "none",
              fontSize: "13px",
            }}
          >
            {t("messaging.delete", "Delete")}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {t("messaging.subject", "Subject")}
              </TableCell>
              <TableCell
                align="center"
                width={180}
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {t("messaging.date", "Date")}
              </TableCell>
              <TableCell
                align="center"
                width={150}
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {t("messaging.remark", "Remark")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    color: textColor,
                    py: 6,
                  }}
                >
                  <Typography variant="body2">
                    {t("messaging.nomessages", "No messages")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              messages.map((msg) => (
                <TableRow
                  key={msg.id}
                  hover
                  onClick={() => onMessageClick(msg.id)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}08`,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(msg.id)}
                      onChange={() => onToggleSelect(msg.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        color: iconColor,
                        "& .MuiSvgIcon-root": { color: iconColor },
                        "&.Mui-checked .MuiSvgIcon-root": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: textColor,
                        fontWeight: msg.isRead ? 400 : 700,
                        fontSize: "13px",
                      }}
                    >
                      {msg.subject}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: textColor,
                        fontSize: "12px",
                        opacity: 0.8,
                      }}
                    >
                      {formatDate(msg.date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title={msg.isPinned ? t("messaging.unpin", "Unpin") : t("messaging.pin", "Pin")}>
                        <IconButton
                          size="small"
                          onClick={(e) => handlePin(msg.id, e)}
                          sx={{ color: iconColor }}
                        >
                          {msg.isPinned ? (
                            <PushPin fontSize="small" sx={{ color: iconColor }} />
                          ) : (
                            <PushPinOutlined fontSize="small" sx={{ color: iconColor }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("messaging.markasread", "Mark As Read")}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMarkAsRead(msg.id, e)}
                          disabled={msg.isRead}
                          sx={{ color: iconColor }}
                        >
                          <MarkunreadMailbox fontSize="small" sx={{ color: iconColor }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("messaging.delete", "Delete")}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(msg.id, e)}
                          sx={{ color: iconColor }}
                        >
                          <DeleteIcon fontSize="small" sx={{ color: iconColor }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" py={2}>
          <Typography
            variant="body2"
            sx={{
              color: textColor,
              fontSize: "12px",
              mr: 2,
              alignSelf: "center",
            }}
          >
            {`Showing ${(page - 1) * 10 + 1} - ${Math.min(page * 10, totalElements)} of ${totalElements} items.`}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                color: textColor,
              },
              "& .Mui-selected": {
                backgroundColor: `${theme.palette.primary.main} !important`,
                color: "white",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MessageTable;
